// TermsConditions.jsx
import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const TermsConditions = () => {
  const navigate = useNavigate();

  const handleGoBack = () => {
    if (window.history.length <= 1) {
      navigate('/', { replace: true }); // cambia '/' se vuoi un'altra pagina di fallback
    } else {
      navigate(-1);
    }
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') handleGoBack();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-800 to-indigo-900 py-12 px-4 sm:px-6 lg:px-8 relative">
      {/* Freccia indietro fissa in alto a sinistra */}
      <button
        onClick={handleGoBack}
        aria-label="Torna alla pagina precedente"
        title="Torna indietro"
        className="fixed top-4 left-4 z-50 inline-flex items-center gap-2 rounded-lg border border-gray-700 bg-slate-900/70 px-3 py-2 text-gray-100 backdrop-blur-sm hover:bg-slate-800/80 focus:outline-none focus:ring-2 focus:ring-blue-400"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span className="hidden sm:inline">Back</span>
      </button>

      <div className="max-w-3xl mx-auto bg-gray-800/50 rounded-xl shadow-lg overflow-hidden p-8 backdrop-blur-sm border border-gray-700">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-blue-300 mb-2">Terms and Conditions</h1>
          <p className="text-gray-400">Last updated: {new Date().toLocaleDateString('en-GB')}</p>
        </div>

        <div className="prose prose-invert max-w-none text-gray-300 space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-blue-200 mb-4">1. Acceptance of Terms</h2>
            <p>
              By accessing or using the services provided by Sweet Trading and Candy Bot Pro (the "Services"), you agree to be bound by these Terms and Conditions and all applicable laws and regulations. If you do not agree with any part of these Terms, you must not use the Services.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-blue-200 mb-4">2. Modifications to Terms</h2>
            <p>
              We reserve the right to modify, amend, or update these Terms at any time without prior notice. Changes will be posted on this page and become effective immediately upon posting. It is your responsibility to review these Terms periodically. Continued use of the Services after changes constitutes your acceptance of the updated Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-blue-200 mb-4">3. Use of Services</h2>
            <p>
              You agree to use the Services only for lawful purposes and in accordance with these Terms. You shall not use the Services to engage in any fraudulent, harmful, or illegal activities.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-blue-200 mb-4">4. Accounts and Security</h2>
            <p>
              To access certain features, you may be required to create an account. You are responsible for maintaining the confidentiality of your account credentials, and for all activities under your account. You agree to notify us immediately of any unauthorized use or suspected security breach.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-blue-200 mb-4">5. Intellectual Property</h2>
            <p>
              All content, trademarks, logos, and intellectual property rights related to Sweet Trading and Candy Bot Pro are owned by or licensed to us. You may not use, reproduce, or distribute any content without our prior written consent.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-blue-200 mb-4">6. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, Sweet Trading shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of the Services. We do not guarantee specific results or profits from using Candy Bot Pro.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-blue-200 mb-4">7. Disclaimer of Warranties</h2>
            <p>
              The Services are provided "as is" and "as available" without warranties of any kind, whether express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-blue-200 mb-4">8. Termination</h2>
            <p>
              We may suspend or terminate your access to the Services at any time, without notice, for conduct that we believe violates these Terms or is harmful to other users or us.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-blue-200 mb-4">9. Privacy</h2>
            <p>
              Your use of the Services is also governed by our <Link to="/privacy" className="text-blue-400 underline">Privacy Policy</Link>, which explains how we collect, use, and protect your personal data.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-blue-200 mb-4">10. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless Sweet Trading, its affiliates, and their respective officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, or expenses arising from your violation of these Terms or misuse of the Services.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-blue-200 mb-4">11. Governing Law and Jurisdiction</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of Italy. Any disputes arising out of or related to these Terms or the Services shall be subject to the exclusive jurisdiction of the courts located in Rome, Italy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-blue-200 mb-4">12. Severability</h2>
            <p>
              If any provision of these Terms is found to be invalid or unenforceable, that provision shall be enforced to the maximum extent possible and the remaining provisions shall remain in full force and effect.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-blue-200 mb-4">13. Entire Agreement</h2>
            <p>
              These Terms, together with our Privacy Policy and any other legal notices published by us, constitute the entire agreement between you and Sweet Trading regarding the Services.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-blue-200 mb-4">14. Contact Us</h2>
            <p>
              If you have any questions about these Terms, please contact us at:<br/>
              <address className="not-italic">
                Sweet Trading<br/>
                Email: <a href="mailto:support@sweet-trading.com" className="text-blue-400 underline">support@sweet-trading.com</a><br/>
              </address>
            </p>
          </section>
        </div>

        <div className="mt-12 pt-6 border-t border-gray-700 text-center">
          <Link 
            to="/privacy" 
            className="text-blue-400 hover:text-blue-300 inline-flex items-center"
          >
            See also our Privacy Policy
            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TermsConditions;
